<template>
  <div v-click-outside="onClickOutside" :id="$attrs['id']" :class="[[$attrs['class']]]" class="hm-notifications">
    <div class="hm-notifications__icon" @click="openNotifications">
      <div :class="[{'hm-notifications__icon__inner--has-notifications': notificationCount > 0 }]" class="hm-notifications__icon__inner">
        <icon icon="fill-notification"></icon>
      </div>
      <div v-if="notificationCount > 0" class="hm-notifications__count">{{ displayNotificationCount }}</div>
    </div>
    <transition-fade duration="0.15">
      <div v-show="showNotifications" class="hm-notifications__content">
        <div v-t="'notifications.title'" class="hm-notifications__content__title"></div>
        <div class="hm-notifications__content__list">
          <a v-for="notification in notifications.unread" class="hm-notifications__content__list__item" @click="onClickNotification(notification)">
            <div v-if="notification.type != null" :title="$t(`NotificationType.${notification.type}`, notification.params)" class="privacy--blur">
              {{ $t(`NotificationType.${notification.type}`, preProcessNotificationParams(notification.type, notification.params)) }}
            </div>
            <div v-else :title="notification.message" class="privacy--blur">
              {{ notification.message }}
            </div>
            <div class="hm-notifications__content__list__item__time">
              {{ notification.dateTime | toDuration }}
            </div>
          </a>
          <a v-if="!isEmpty(notifications) && notificationCount - notifications.unread.length > 0" class="hm-notifications__content__list__item" @click="routeToNotifications">
            <div>
              {{ $t('notifications.further_unread', [(notificationCount - notifications.unread.length)]) }}
            </div>
            <div class="hm-notifications__content__list__item__time"></div>
          </a>
        </div>
        <div class="hm-notifications__content__footer">
          <div class="hm-notifications__content__footer__show-all" @click="routeToNotifications">{{ $t('notifications.showAll') }}</div>
          <div v-if="notificationCount > 0" class="hm-notifications__content__footer__mark-completed" @click="markAllAsRead">{{ $t('notifications.markAllAsRead') }}
            <icon icon="save"></icon>
          </div>
        </div>
      </div>
    </transition-fade>
  </div>
</template>

<script>

import { isNil } from 'lodash';
import ServiceUrls from '@/lib/ServiceUrls';
import NotificationMixin from '../../../mixins/NotificationMixin';
import HotelmeisterMixin from '../../../mixins/HotelmeisterMixin';
import UpdateHotelSettingsMixin from '../../../mixins/UpdateHotelSettingsMixin';
import TimerMixin from '../../../mixins/TimerMixin';
import ReloadPageMixin from '../../../mixins/ReloadPageMixin';

export default {
  name: 'HmNotifications',

  mixins: [HotelmeisterMixin, NotificationMixin, UpdateHotelSettingsMixin, TimerMixin, ReloadPageMixin],

  data: function () {
    return {
      showNotifications: false,
      serviceUrls: ServiceUrls,
    };
  },

  computed: {
    notifications: function () {
      return this.$store.getters['notifications/notifications'];
    },
    notificationCount: function () {
      return this.notifications.count;
    },
    displayNotificationCount: function () {
      return this.notifications.count;
    },
    activeBreakpoint: function () {
      return this.$store.getters['layout/activeBreakpoint'];
    },
  },

  created() {
    // prime number = 314933 --> this is to ensure that we do not refresh in the same interval as keepAlive (thereby reducing server load)
    this.setInterval(this.updateNotifications, 5 * 60 * 1000 + 14933);
  },

  methods: {
    openNotifications: function () {
      if (this.activeBreakpoint === 'mobile' || this.activeBreakpoint === 'tablet') {
        this.routeToNotifications();
      } else {
        this.showNotifications = !this.showNotifications;
      }
    },
    routeToNotifications: function () {
      if (this.$route.name === 'notifications') {
        this.reloadPage();
        this.showNotifications = false;
      } else {
        this.$router.push({ name: 'notifications' });
        this.showNotifications = false;
      }
    },
    onClickOutside: function ($event) {
      this.showNotifications = false;
    },
    onClickNotification: function (notification) {
      this.openEntity(
        isNil(notification.entityIdentifier) ? null : notification.entityIdentifier.type,
        isNil(notification.entityIdentifier) ? null : notification.entityIdentifier.id,
        notification.id,
      );
    },
  },
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss" scoped>
  @import '~styles/import';

  .hm-notifications {
    position: relative;
    user-select: none;

    &__icon {
      width: 44px;
      height: $admin-header-height;
      display: inline-block;
      position: relative;

      &__inner {
        display: grid;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;

        &--has-notifications {
          animation: bell-shake 10s ease infinite;
        }

      }

      .hm-icon {
        width: 32px;
        height: 32px;
      }

      &:hover {
        cursor: pointer;
      }
    }

    &__count {
      position: absolute;
      right: 0;
      bottom: 8px;
      background-color: $error;
      border-radius: 10px;
      padding: 4px 6px;
      width: auto;
      line-height: 20px;
      display: grid;
      align-items: center;
      justify-items: center;
      line-height: 1;
      font-size: 1.2rem;
      color: $white;
      box-shadow: 0 2px 2px rgba($black, 0.2);
    }

    &__content {
      width: 500px;
      max-width: calc(100vw - 30px);
      background-color: $white;
      border-radius: 3px;
      position: absolute;
      right: -10px;
      top: calc(100% + 5px);
      text-align: left;
      box-shadow: 0 0 2rem rgba($black, 0.1);
      z-index: 1000;

      &:before {
        content: '';
        position: absolute;
        display: block;
        border-style: solid;
        border-width: 0 1rem 1rem 1rem;
        border-color: transparent transparent $white transparent;
        top: -1rem;
        right: 2rem;
        z-index: 100;

      }

      &__title {
        padding: 10px 20px;
        color: $text;
        font-weight: 700;
      }

      &__list {
        max-height: 300px;
        overflow-y: auto;

        &__item {
          padding: 10px 20px;
          background-color: $lightgray;
          color: $text;
          display: grid;
          grid-template-columns: 1fr 40px;
          line-height: 1.55;
          position: relative;

          &:before {
            content: '';
            width: 100%;
            height: 1px;
            position: absolute;
            left: 0;
            top: 0;
            background-color: $middlemint;
            display: block;
          }

          &:first-of-type {
            &:before {
              content: none;
            }
          }

          &:hover {
            background-color: $lightmint;
            cursor: pointer;

            &:before {
              content: '';
              width: 100%;
              height: 1px;
              position: absolute;
              left: 0;
              top: 0;
              background-color: $middlemint;
              display: block;
            }
          }

          &__time {
            text-align: right;
          }
        }
      }

      &__footer {
        color: $mint;
        display: grid;
        grid-template-columns: 1fr 1fr;
        border-top: 1px solid $middlegray;
        padding: 10px 20px;

        &__show-all {
          font-weight: 700;
          transition: color 0.2s ease;

          &:hover {
            color: $text;
            cursor: pointer;
          }
        }

        &__mark-completed {
          text-align: right;
          padding-right: 40px;
          position: relative;
          transition: color 0.2s ease;

          &:hover {
            color: $text;
            cursor: pointer;
          }

          /deep/ .hm-icon {
            position: absolute;
            right: 0;
            top: -12px;
            width: 37px;
            height: 37px;
            display: inline-block;

            svg * {
              stroke: $mint;
            }
          }
        }
      }
    }
  }

  @keyframes bell-shake {
    4% {
      transform: rotateZ(15deg);
      transform-origin: 50% 0%;
    }
    8% {
      transform: rotateZ(-15deg);
      transform-origin: 50% 0%;
    }
    12% {
      transform: rotateZ(20deg);
      transform-origin: 50% 0%;
    }
    16% {
      transform: rotateZ(-20deg);
      transform-origin: 50% 0%;
    }
    20% {
      transform: rotateZ(15deg);
      transform-origin: 50% 0%;
    }
    24% {
      transform: rotateZ(-15deg);
      transform-origin: 50% 0%;
    }
    28% {
      transform: rotateZ(0);
      transform-origin: 50% 0%;
    }
    100% {
      transform: rotateZ(0);
      transform-origin: 50% 0%;
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/lib/notifications/HmNotifications.vue